/**
 * Normalizes a path segment: trims, lowercases, and replaces whitespace runs with underscores.
 */
function normalizeSegment(segment: string): string {
    return segment.trim().toLowerCase().replace(/\s+/g, '_');
}

/**
 * Computes all bucket paths as a cartesian product of the provided location, client,
 * and (optionally) supervisor arrays.
 *
 * - Empty strings (after trim) are filtered out before computation.
 * - Each segment is normalized to lowercase with spaces replaced by underscores.
 * - If supervisors are provided, paths follow `itel/<location>/<client>/<supervisor>`.
 * - Otherwise, paths follow `itel/<location>/<client>`.
 * - If locations or clients are empty after filtering, returns an empty array.
 */
export function computeBucketPaths(
    locations: string[],
    clients: string[],
    supervisors: string[],
    _roleName: string,
    _email: string
): string[] {
    const filteredLocations = locations.filter((s) => s.trim().length > 0);
    const filteredClients = clients.filter((s) => s.trim().length > 0);
    const filteredSupervisors = supervisors.filter((s) => s.trim().length > 0);

    if (filteredLocations.length === 0 || filteredClients.length === 0) {
        return [];
    }

    // Include supervisor in path when supervisors are selected
    if (filteredSupervisors.length > 0) {
        const paths: string[] = [];
        for (const loc of filteredLocations) {
            for (const client of filteredClients) {
                for (const sup of filteredSupervisors) {
                    paths.push(
                        `itel/${normalizeSegment(loc)}/${normalizeSegment(client)}/${normalizeSegment(sup)}`
                    );
                }
            }
        }
        return paths;
    }

    // No supervisor — produce locations × clients only
    const paths: string[] = [];
    for (const loc of filteredLocations) {
        for (const client of filteredClients) {
            paths.push(`itel/${normalizeSegment(loc)}/${normalizeSegment(client)}`);
        }
    }
    return paths;
}
